import React from "react";
import SectionWithButtonsLayout from "../../shared/layout/SectionWithButtonsLayout";
import TogalDatasourceCard from "./togal/TogalDatasourceCard";
import AtoDatasourceCard from "./ato/AtoDatasourceCard";
import OstDatasourceCard from "./ost/OstDatasourceCard";
import { TakeOffSourceType, useDatasources } from "../../context/DatasourceContext";
import { useLoading } from "../../context/LoadingContext";
import { useProject } from "../../context/ProjectContext";
import { useQtoSourcesEnabled } from "../../hooks/useQtoSources";
import { QTO_SOURCE_ORDER } from "../../../integrations/qto/registry";

interface TakeOffDatasourcesSectionProps {
  handleAddClick: () => void;
  handleEditClick: (type: TakeOffSourceType) => void;
}

const CARDS: Partial<Record<TakeOffSourceType, React.ComponentType<{ onCardClick: () => void }>>> = {
  togal: TogalDatasourceCard,
  ato: AtoDatasourceCard,
  ost: OstDatasourceCard,
};

const TakeOffDatasourcesSection = ({ handleAddClick, handleEditClick }: TakeOffDatasourcesSectionProps) => {
  const { takeOffSources, hasDatasource } = useDatasources();
  const { globalLoading } = useLoading();
  const { selectedEstimate } = useProject();
  const enabledById = useQtoSourcesEnabled();

  const enabledSources = QTO_SOURCE_ORDER.filter((type) => enabledById[type]);
  // Take-off quantities land in an estimate, so there is nothing to add a source to without one.
  const canAddSource = !!selectedEstimate && enabledSources.some((type) => !hasDatasource("takeOff", type));

  const renderCards = () => {
    const cards = takeOffSources
      .map((type) => {
        const Card = CARDS[type];
        return Card ? <Card key={type} onCardClick={() => handleEditClick(type)} /> : null;
      })
      .filter(Boolean);

    return cards.length > 0 ? cards : null;
  };

  // A section offering nothing is worse than no section, so it goes rather than sitting there disabled.
  if (enabledSources.length === 0) return null;

  // FIXME: The empty state text is a placeholder. We need to confirm what the actual text should be.
  return (
    <SectionWithButtonsLayout
      header={{ text: "External Takeoff Sources" }}
      primaryButton={{ onClick: handleAddClick, disabled: !canAddSource || globalLoading }}
      emptyStateText={
        selectedEstimate
          ? "Add an External Takeoff Source to bring measured quantities into your Ediphi estimate."
          : "Load estimate data to get started."
      }
    >
      {renderCards()}
    </SectionWithButtonsLayout>
  );
};

export default TakeOffDatasourcesSection;
